import { z } from 'zod';

const toNumberOrNull = (val: unknown) =>
  val === '' || val === undefined || val === null ? null : Number(val);

export const createProductFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  form: z.string().min(1, 'Format is required'),
  category: z.string().min(1, 'Category is required'),
});

export type CreateProductFormData = z.infer<typeof createProductFormSchema>;

export const updateProductFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  category: z.string().min(1, 'Category is required'),
  form: z.string().min(1, 'Format is required'),
  catalogue_ids: z.array(z.number()).default([]),
  active_start_date: z.string().nullable().optional(),
  active_end_date: z.string().nullable().optional(),
  price_excluding_gst: z.preprocess(toNumberOrNull, z.number().nonnegative().nullable().optional()),
  cost_to_company: z.preprocess(toNumberOrNull, z.number().nonnegative().nullable().optional()),
  chinese_name: z.string().nullable().optional(),
  byline: z.string().nullable().optional(),
  ingredients: z.string().nullable().optional(),
  serving_temperature: z.string().nullable().optional(),
  kitchen: z.string().nullable().optional(),
  kitchen_section: z.array(z.string()).default([]),
  papercut_category: z.string().nullable().optional(),
  portion_amount: z.preprocess(toNumberOrNull, z.number().nonnegative().nullable().optional()),
  portion_unit_label: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  production_notes: z.string().nullable().optional(),
  fulfilment_notes: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  calories: z.preprocess(toNumberOrNull, z.number().int().nonnegative().nullable().optional()),
  carbohydrates: z.preprocess(toNumberOrNull, z.number().int().nonnegative().nullable().optional()),
  fats: z.preprocess(toNumberOrNull, z.number().int().nonnegative().nullable().optional()),
  protein: z.preprocess(toNumberOrNull, z.number().int().nonnegative().nullable().optional()),
  diet_tag_list: z.array(z.string()).default([]),
  allergen_tag_list: z.array(z.string()).default([]),
  nutrition_tag_list: z.array(z.string()).default([]),
  chef_recommended: z.boolean().default(false),
  bcrs: z.boolean().default(false),
  display_unit: z.string().default('item'),
  product_inventories: z.array(z.object({
    id: z.number().optional(),
    inventory_id: z.number(),
    inventory_name: z.string(),
    inventory_category: z.string().nullable(),
    quantity: z.preprocess(toNumberOrNull, z.number().int().nonnegative().nullable()),
  })).default([]),
  product_containers: z.array(z.object({
    id: z.number().optional(),
    inventory_id: z.number(),
    inventory_name: z.string(),
    inventory_category: z.string().nullable(),
    capacity: z.preprocess(toNumberOrNull, z.number().int().nonnegative().nullable()),
    minimum_pax_required: z.preprocess(toNumberOrNull, z.number().int().nonnegative().nullable()),
  })).default([]),
  stocks: z.array(z.object({
    id: z.number().optional(),
    quantity: z.preprocess(toNumberOrNull, z.number().int().nonnegative().nullable()),
    start_date: z.string().nullable(),
    end_date: z.string().nullable(),
    no_end_date: z.boolean().default(false),
  })).default([]),
  _imageVersion: z.number().default(0),
  _displayAttachmentVersion: z.number().default(0),
});

export type UpdateProductFormData = z.infer<typeof updateProductFormSchema>;

// ── Publish validation ──────────────────────────────────────────────────────
// Stricter refinement layer on top of the update schema.
// Mirrors the backend Product model's `with_options if: :published?` block.

const PORTION_FORMATS = ['buffet', 'mini_buffet', 'gathering_box'];
const MACROS_FORMATS_PUBLISH = ['meal_box', 'treat_box'];

export const publishProductFormSchema = updateProductFormSchema.superRefine((data, ctx) => {
  const issue = (path: string, message: string) =>
    ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });

  if (!data.catalogue_ids || data.catalogue_ids.length === 0) {
    issue('catalogue_ids', 'At least one catalogue is required');
  }
  if (data.price_excluding_gst == null) {
    issue('price_excluding_gst', 'Price before GST is required');
  }
  if (data.cost_to_company == null) {
    issue('cost_to_company', 'Food cost is required');
  }
  if (!data.byline) {
    issue('byline', 'Byline is required');
  }
  if (!data.ingredients) {
    issue('ingredients', 'Ingredients is required');
  }
  if (!data.serving_temperature) {
    issue('serving_temperature', 'Serving temperature is required');
  }

  const hasTags =
    (data.diet_tag_list?.length ?? 0) > 0 ||
    (data.allergen_tag_list?.length ?? 0) > 0 ||
    (data.nutrition_tag_list?.length ?? 0) > 0;
  if (!hasTags) {
    issue('diet_tag_list', 'At least one tag is required');
  }

  if (PORTION_FORMATS.includes(data.form)) {
    if (data.portion_amount == null) {
      issue('portion_amount', 'Portion size is required');
    }
  }

  if (MACROS_FORMATS_PUBLISH.includes(data.form)) {
    if (data.calories == null) issue('calories', 'Calories is required');
    if (data.protein == null) issue('protein', 'Protein is required');
    if (data.fats == null) issue('fats', 'Fat is required');
    if (data.carbohydrates == null) issue('carbohydrates', 'Carbs is required');
  }
});
